import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AdminLayout from "../../layouts/AdminLayout";
import {
  RunsStatus,
  TeamRunCard,
  TeamRunCardSkeleton,
} from "../../components/Admin/CommandAndControlCards";
import GlobalInjectPanel, {
  GlobalInjectPanelSkeleton,
} from "../../components/Admin/GlobalInjectPanel";
import ScenarioBriefing, {
  ScenarioBriefingSkeleton,
} from "../../components/Admin/ScenarioBriefing";
import { useInjectionRuns } from "../../hooks/useInjectionRuns";

export default function CommandAndControlScenario() {
  const { scenarioId } = useParams();
  const { runs, setRuns, isLoading, loadError } = useInjectionRuns();
  // No per-scenario endpoint exists yet, so filter the all-runs listing.
  const scenarioRuns = runs.filter((run) => run.scenario_id === scenarioId);
  const scenario = scenarioRuns[0]?.scenario;

  // Reflect a successful inject without a full refetch.
  const handleStageAdvanced = (runId, newStage) => {
    setRuns((prev) =>
      prev.map((r) =>
        r.id === runId ? { ...r, current_stage: newStage } : r,
      ),
    );
  };

  return (
    <AdminLayout>
      <div className="w-full px-8 py-8 space-y-6 min-h-screen">
        <Link
          to="/admin/command-and-control"
          className="inline-flex items-center gap-1.5 text-xs font-medium text-(--color-text-muted) hover:text-(--color-primary) transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to Command and Control
        </Link>

        {isLoading ? (
          <>
            <ScenarioBriefingSkeleton />
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <TeamRunCardSkeleton key={i} />
                ))}
              </div>
              <GlobalInjectPanelSkeleton />
            </div>
          </>
        ) : (
          <>
            <ScenarioBriefing
              scenario={scenario}
              totalStages={scenarioRuns[0]?.total_stages ?? 0}
              teamCount={scenarioRuns.length}
            />

            <div className="space-y-3">
          {!loadError && scenarioRuns.length > 0 && (
            <p className="text-sm">
              <span className="font-semibold text-(--color-text-primary)">
                Teams
              </span>
              <span className="ml-2 text-(--color-text-muted)">
                {scenarioRuns.length} on this scenario
              </span>
            </p>
          )}

          {/* Panel is bounded + sticky; team list scrolls independently. */}
          <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem] items-start">
            <div className="min-w-0">
              <RunsStatus
                isLoading={isLoading}
                loadError={loadError}
                isEmpty={scenarioRuns.length === 0}
                emptyTitle="No live teams"
                emptyText="No team is currently running this scenario."
              />

              {!loadError && scenarioRuns.length > 0 && (
                <div className="space-y-4">
                  {scenarioRuns.map((run) => (
                    <TeamRunCard
                      key={run.id}
                      run={run}
                      onStageAdvanced={handleStageAdvanced}
                    />
                  ))}
                </div>
              )}
            </div>

            {!loadError && scenarioRuns.length > 0 && (
              <GlobalInjectPanel
                runs={scenarioRuns}
                scenarioId={scenarioId}
                onStageAdvanced={handleStageAdvanced}
              />
            )}
          </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
